// Copyright 2025 - Hewlett Packard Enterprise Company
import React, { createContext, useCallback, useMemo, useState } from 'react';

import useFlags from '../hooks/useFlags';

export const SidebarContext = createContext();

export default function SidebarProvider({ children }) {
  const flags = useFlags();
  const chatBotFlag = flags.T_20241210_53281_CHATBOT_SUPPORT_BOOL();

  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isContextualHelpOpen, setIsContextualHelpOpen] = useState(() => {
    document.body.className.split(' ').includes('helpOpen');
  });

  const contextualHelpOpen = useCallback(isOpen => {
    if (isOpen) {
      // close other sidebars
      setIsChatOpen(false);

      setIsContextualHelpOpen(true);
    } else {
      setIsContextualHelpOpen(false);
    }
  }, []);

  const chatOpen = useCallback(
    isOpen => {
      if (isOpen) {
        // close other sidebars
        if (isContextualHelpOpen) {
          dispatchEvent(new Event('context-help'));
        }

        setIsChatOpen(true);
      } else {
        setIsChatOpen(false);
      }
    },
    [isContextualHelpOpen],
  );

  const isSidebarOpen = useMemo(
    () => !!((chatBotFlag && isChatOpen) || isContextualHelpOpen),
    [chatBotFlag, isChatOpen, isContextualHelpOpen],
  );

  const toggleIsChatOpen = useCallback(() => {
    chatOpen(!isChatOpen);
  }, [chatOpen, isChatOpen]);

  const toggleIsContextualHelpOpen = useCallback(() => {
    dispatchEvent(new Event('context-help'));
  }, []);

  const value = useMemo(
    () => ({
      isChatOpen: chatBotFlag && isChatOpen,
      setIsChatOpen: chatOpen,
      isContextualHelpOpen,
      setIsContextualHelpOpen: contextualHelpOpen,
      isSidebarOpen,
      toggleIsChatOpen,
      toggleIsContextualHelpOpen,
    }),
    [
      chatBotFlag,
      chatOpen,
      contextualHelpOpen,
      isChatOpen,
      isContextualHelpOpen,
      isSidebarOpen,
      toggleIsChatOpen,
      toggleIsContextualHelpOpen,
    ],
  );

  // the current design is to only allow a single side bar panel to be open at a time
  return (
    <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>
  );
}
